/* Privacy Policy , static, cream theme matching FAQ/Contact pages.
   Plain-English, Australia-focused (Privacy Act 1988 / APPs). Real Magic Cruises
   contact details from cruiseData.js. */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowRight, Shield } from "lucide-react";
import { CONTACT } from "../data/cruiseData";
import SEOHead from "../components/SEOHead";

const SECTIONS = [
 {
  id: "who-we-are",
  title: "Who we are",
  body: [
   "Magic Cruises Pty Ltd operates the MV Sydney Magic and the website at magiccruises.com.au. In this Privacy Policy, references to 'we', 'us' and 'our' mean Magic Cruises. Our principal place of business is 5A Phoebe St, Balmain NSW.",
   "This Privacy Policy explains how we collect, use, store and disclose your personal information when you visit our website, enquire about a cruise, book a ticket or contact our team.",
  ],
 },
 {
  id: "information-we-collect",
  title: "Information we collect",
  body: [
   "We collect personal information you provide directly when you submit an enquiry form, purchase a ticket through Stripe Checkout, sign up to receive event updates, or contact us by phone or email.",
   "The personal information we typically collect includes your full name, email address, mobile phone number, event type, preferred date, guest count and any notes you share about your booking.",
   "When you purchase tickets, payment is handled by Stripe. We do not store your full credit card number, CVC or bank details on our servers. We retain the Stripe session reference and the order summary (event, ticket count, total amount) so we can issue your boarding pass PDF and answer follow-up questions about your booking.",
   "We also collect technical information automatically when you visit the site, including your IP address, browser type, referring page and pages visited. This is used to keep the site secure, debug issues and understand which pages are most useful.",
  ],
 },
 {
  id: "how-we-use",
  title: "How we use your information",
  body: [
   "We use your personal information to respond to enquiries, prepare custom cruise quotes, process ticket bookings, issue boarding passes, send booking confirmations, communicate weather or operational updates that affect your cruise, and provide post-cruise follow-up.",
   "If you opt in, we may also send you occasional updates about new ticketed events, seasonal cruises (Vivid, NYE, Australia Day, Boxing Day) and special offers. You can opt out at any time by replying 'unsubscribe' to any email or by emailing info@magiccruises.com.au.",
  ],
 },
 {
  id: "sharing",
  title: "Who we share information with",
  body: [
   "We do not sell or rent your personal information. We share information only with the following trusted service providers, strictly to deliver our cruise service:",
   "Stripe (Stripe Payments Australia Pty Ltd) processes ticket payments and stores card details under PCI-DSS compliance. MongoDB Atlas (or equivalent secure database hosting) stores enquiry and order records. Resend / SendGrid (or equivalent transactional email provider) delivers booking confirmation emails when configured. Google Analytics may collect anonymised usage statistics if enabled.",
   "We may also disclose personal information where required by Australian law, for example to comply with a subpoena, court order or law enforcement request.",
  ],
 },
 {
  id: "cookies",
  title: "Cookies and tracking",
  body: [
   "We use a small number of functional cookies to remember your session and keep the booking flow working between pages. We may also use analytics cookies to understand site usage in aggregate. We do not use third-party advertising cookies that track you across other websites.",
   "You can clear or block cookies at any time in your browser settings. The site will continue to work, although some features (such as remembering items in your booking) may be limited.",
  ],
 },
 {
  id: "storage",
  title: "How long we keep your information",
  body: [
   "Enquiry submissions are typically retained for 24 months from the date received so we can support repeat enquiries and event planning. Ticket booking records are retained for 7 years to meet Australian tax and business record-keeping requirements. Marketing list subscribers are retained until they opt out.",
   "Where information is no longer required and we have no legal obligation to retain it, we delete it from our systems.",
  ],
 },
 {
  id: "security",
  title: "How we protect your information",
  body: [
   "Personal information is held on encrypted, password-protected servers hosted in Australia or via providers that meet Australian Privacy Principles. Payments are processed via Stripe's PCI-DSS compliant infrastructure. Access to enquiry data is restricted to authorised Magic Cruises staff via password-protected admin tools.",
   "While we take reasonable steps to protect personal information from misuse, loss, unauthorised access, modification and disclosure, no method of transmission over the internet is completely secure. You provide your information at your own risk.",
  ],
 },
 {
  id: "your-rights",
  title: "Your rights",
  body: [
   "Under the Australian Privacy Principles (APPs) you have the right to access the personal information we hold about you, request corrections to information that is inaccurate or out of date, request deletion where we are not required by law to retain it, and lodge a complaint about how we have handled your information.",
   "To exercise any of these rights, email info@magiccruises.com.au with the subject line 'Privacy request'. We respond within 30 days. If you are not satisfied with our response, you may contact the Office of the Australian Information Commissioner (oaic.gov.au).",
  ],
 },
 {
  id: "children",
  title: "Children's privacy",
  body: [
   "Our website and ticketing service is intended for adults aged 18 and over. We do not knowingly collect personal information from children under 16 without parent or guardian consent. Children attending family cruises are booked under the adult's name and we collect only the count and any dietary requirements, not their personal details.",
  ],
 },
 {
  id: "changes",
  title: "Changes to this policy",
  body: [
   "We may update this Privacy Policy from time to time. The current version is always available at magiccruises.com.au/privacy. Substantive changes that affect your rights will be notified by email to enquiry subscribers and by a prominent notice on the website.",
   "This policy was last updated in February 2026.",
  ],
 },
];


export default function Privacy() {
 useEffect(() => { document.title = "Privacy Policy | Magic Cruises Sydney"; }, []);
 return (
  <main className="pt-32 pb-24" data-testid="privacy-page">
   <SEOHead title="Privacy Policy | Magic Cruises Sydney" description="How Magic Cruises collects, uses and protects your personal information under the Australian Privacy Principles." canonical="https://magiccruises.com.au/privacy" />

   <div className="mx-auto max-w-3xl px-5 lg:px-10">
    <div className="eyebrow inline-flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-pink" /> Legal</div>
    <h1 className="font-display text-4xl lg:text-7xl mt-3 mb-5 text-ink leading-[1]" data-testid="privacy-h1">
     Privacy <span className="text-pink italic">Policy</span>.
    </h1>
    <p className="text-ink-soft text-base lg:text-lg leading-relaxed mb-2">
     Plain-English summary of how we handle your information. We collect what we need, store it safely, never sell it, and you can ask us to delete it at any time.
    </p>
    <p className="text-ink-soft/70 text-xs uppercase tracking-[0.22em] font-bold mb-12">Last updated · February 2026</p>

    {/* Quick anchor nav */}
    <nav aria-label="Privacy policy sections" className="mb-12 rounded-2xl border border-[hsla(222,20%,80%,0.5)] bg-white/60 p-5" data-testid="privacy-toc">
     <div className="text-[10px] uppercase tracking-[0.28em] font-black text-ink-soft mb-3">On this page</div>
     <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {SECTIONS.map((s) => (
       <li key={s.id}>
        <a href={`#${s.id}`} className="text-ink hover:text-pink transition-colors inline-flex items-center gap-1.5 group" data-testid={`privacy-toc-${s.id}`}>
         <ArrowRight className="w-3 h-3 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" /> {s.title}
        </a>
       </li>
      ))}
     </ul>
    </nav>

    <div className="space-y-12">
     {SECTIONS.map((s) => (
      <section key={s.id} id={s.id} className="scroll-mt-32" data-testid={`privacy-section-${s.id}`}>
       <h2 className="font-display text-2xl lg:text-3xl text-ink mb-4 leading-tight">{s.title}</h2>
       {s.body.map((p, i) => (
        <p key={i} className="text-ink-soft text-base leading-relaxed mb-3">{p}</p>
       ))}
      </section>
     ))}
    </div>

    {/* Contact block */}
    <div className="mt-16 rounded-2xl bg-white border border-[hsla(222,20%,80%,0.5)] p-7 lg:p-9" data-testid="privacy-contact">
     <div className="eyebrow mb-3">Questions about your privacy?</div>
     <h3 className="font-display text-2xl lg:text-3xl text-ink mb-5">Talk to us.</h3>
     <div className="space-y-3 text-sm font-medium">
      <a href={`mailto:${CONTACT.email}?subject=Privacy%20request`} className="flex items-center gap-3 text-ink hover:text-pink"><Mail className="w-4 h-4 text-pink" /> {CONTACT.email}</a>
      <a href={`tel:${CONTACT.phone1}`} className="flex items-center gap-3 text-ink hover:text-pink"><Phone className="w-4 h-4 text-pink" /> {CONTACT.phone1}</a>
      <div className="text-ink-soft text-sm pt-2">Postal · {CONTACT.address}</div>
     </div>
     <div className="mt-6 pt-6 border-t border-[hsla(222,20%,80%,0.5)] flex flex-wrap gap-4 text-sm">
      <Link to="/terms" className="text-pink hover:text-pink/80 font-bold inline-flex items-center gap-1" data-testid="privacy-to-terms">Terms & Conditions <ArrowRight className="w-3 h-3" /></Link>
      <Link to="/contact" className="text-ink hover:text-pink font-bold inline-flex items-center gap-1" data-testid="privacy-to-contact">Contact <ArrowRight className="w-3 h-3" /></Link>
     </div>
    </div>
   </div>
  </main>
 );
}
